import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { createClient } from "@supabase/supabase-js";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import type { Database } from "@/integrations/supabase/types";

export type DoctorRow = {
  id: string;
  slug: string;
  name: string;
  specialty: string;
  specialty_slug: string;
  experience: string;
  hospital: string | null;
  location_slug: string | null;
  gender: "male" | "female";
  qualifications: string | null;
  languages: string[];
  about: string | null;
  image_url: string | null;
  display_order: number;
  published: boolean;
  consult_fee_inhospital: number | null;
  consult_fee_online: number | null;
  schedule_days: string | null;
  schedule_hours: string | null;
  education: string[];
  work_experience: string[];
  meta_title: string | null;
  meta_description: string | null;
};

const SELECT =
  "id,slug,name,specialty,specialty_slug,experience,hospital,location_slug,gender,qualifications,languages,about,image_url,display_order,published,consult_fee_inhospital,consult_fee_online,schedule_days,schedule_hours,education,work_experience,meta_title,meta_description";

// Server-side anon client for public reads — RLS is enforced (no service-role bypass).
let _supabaseAnon: ReturnType<typeof createClient<Database>> | undefined;
function getSupabaseAnon() {
  if (_supabaseAnon) return _supabaseAnon;
  const url = process.env.SUPABASE_URL;
  const key = process.env.SUPABASE_PUBLISHABLE_KEY ?? process.env.SUPABASE_ANON_KEY;
  if (!url || !key) {
    throw new Error("Missing SUPABASE_URL or SUPABASE_PUBLISHABLE_KEY env var");
  }
  _supabaseAnon = createClient<Database>(url, key, {
    auth: { persistSession: false, autoRefreshToken: false, storage: undefined },
  });
  return _supabaseAnon;
}

// ---------------- Public reads (anon client; RLS enforces published = true) ----------------

export const listPublishedDoctors = createServerFn({ method: "GET" }).handler(
  async (): Promise<DoctorRow[]> => {
    try {
      const { data, error } = await getSupabaseAnon()
        .from("doctors")
        .select(SELECT)
        .eq("published", true)
        .order("display_order", { ascending: true })
        .order("name", { ascending: true });
      if (error) throw error;
      return (data ?? []) as DoctorRow[];
    } catch (error) {
      console.error("[doctors-api] listPublishedDoctors degraded to local directory", error);
      return [];
    }
  },
);

export const getDoctorBySlug = createServerFn({ method: "GET" })
  .inputValidator((input) => z.object({ slug: z.string().min(1).max(120) }).parse(input))
  .handler(async ({ data }): Promise<DoctorRow | null> => {
    try {
      const { data: row, error } = await getSupabaseAnon()
        .from("doctors")
        .select(SELECT)
        .eq("slug", data.slug)
        .eq("published", true)
        .maybeSingle();
      if (error) throw error;
      return (row as DoctorRow | null) ?? null;
    } catch (error) {
      console.error(`[doctors-api] getDoctorBySlug(${data.slug}) degraded to local profile`, error);
      return null;
    }
  });

// ---------------- Admin CRUD ----------------

const DoctorInput = z.object({
  slug: z
    .string()
    .min(1)
    .max(120)
    .regex(/^[a-z0-9-]+$/, "Use lowercase letters, numbers and dashes only"),
  name: z.string().min(1).max(200),
  specialty: z.string().min(1).max(200),
  specialty_slug: z.string().min(1).max(120),
  experience: z.string().max(100).default(""),
  hospital: z.string().max(200).nullable().optional(),
  location_slug: z.string().max(120).nullable().optional(),
  gender: z.enum(["male", "female"]),
  qualifications: z.string().max(500).nullable().optional(),
  languages: z.array(z.string().min(1).max(60)).max(20).default([]),
  about: z.string().max(4000).nullable().optional(),
  image_url: z.string().url().max(500).nullable().optional(),
  display_order: z.number().int().min(0).max(100000).default(0),
  published: z.boolean().default(true),
  consult_fee_inhospital: z.number().int().min(0).max(1000000).nullable().optional(),
  consult_fee_online: z.number().int().min(0).max(1000000).nullable().optional(),
  schedule_days: z.string().max(120).nullable().optional(),
  schedule_hours: z.string().max(120).nullable().optional(),
  education: z.array(z.string().min(1).max(300)).max(30).default([]),
  work_experience: z.array(z.string().min(1).max(300)).max(30).default([]),
  meta_title: z.string().max(160).nullable().optional(),
  meta_description: z.string().max(320).nullable().optional(),
});

async function requireAdmin(userId: string) {
  const { data, error } = await supabaseAdmin
    .from("user_roles")
    .select("role")
    .eq("user_id", userId)
    .eq("role", "admin")
    .maybeSingle();
  if (error) throw new Error(error.message);
  if (!data) throw new Error("Forbidden: admin role required");
}

export const adminListDoctors = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<DoctorRow[]> => {
    await requireAdmin(context.userId);
    const { data, error } = await supabaseAdmin
      .from("doctors")
      .select(SELECT)
      .order("display_order", { ascending: true })
      .order("name", { ascending: true });
    if (error) throw new Error(error.message);
    return (data ?? []) as DoctorRow[];
  });

export const adminGetDoctor = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => z.object({ id: z.string().uuid() }).parse(input))
  .handler(async ({ data, context }): Promise<DoctorRow | null> => {
    await requireAdmin(context.userId);
    const { data: row, error } = await supabaseAdmin
      .from("doctors")
      .select(SELECT)
      .eq("id", data.id)
      .maybeSingle();
    if (error) throw new Error(error.message);
    return (row as DoctorRow | null) ?? null;
  });

export const adminCreateDoctor = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => DoctorInput.parse(input))
  .handler(async ({ data, context }) => {
    await requireAdmin(context.userId);
    const { data: row, error } = await supabaseAdmin
      .from("doctors")
      .insert(data)
      .select(SELECT)
      .single();
    if (error) throw new Error(error.message);
    return row as DoctorRow;
  });

export const adminUpdateDoctor = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z.object({ id: z.string().uuid(), values: DoctorInput }).parse(input),
  )
  .handler(async ({ data, context }) => {
    await requireAdmin(context.userId);
    const { data: row, error } = await supabaseAdmin
      .from("doctors")
      .update(data.values)
      .eq("id", data.id)
      .select(SELECT)
      .single();
    if (error) throw new Error(error.message);
    return row as DoctorRow;
  });

export const adminDeleteDoctor = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => z.object({ id: z.string().uuid() }).parse(input))
  .handler(async ({ data, context }) => {
    await requireAdmin(context.userId);
    const { error } = await supabaseAdmin.from("doctors").delete().eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });
